import { idbGet, idbSet, idbDelete, idbKeysByPrefix } from './idb';
import { createLogger } from '@/lib/logger';

const log = createLogger('SyncMeta');

const KEY_PREFIX = 'sync_meta:';

export interface SyncMeta {
  base_id: string;
  last_sync_version: number;
  last_sync_at?: string;
  status: 'idle' | 'syncing' | 'error' | 'conflict';
  error?: string;
}

function metaKey(baseId: string): string {
  return `${KEY_PREFIX}${baseId}`;
}

export async function metaGet(baseId: string): Promise<SyncMeta | undefined> {
  if (!baseId) {
    log.warn('metaGet called without baseId');
    return undefined;
  }
  return idbGet<SyncMeta>(metaKey(baseId));
}

export async function metaSet(baseId: string, meta: Partial<SyncMeta>): Promise<void> {
  if (!baseId) {
    log.warn('metaSet called without baseId');
    return;
  }
  const existing = (await metaGet(baseId)) ?? {
    base_id: baseId,
    last_sync_version: 0,
    status: 'idle',
  };
  await idbSet(metaKey(baseId), { ...existing, ...meta, base_id: baseId });
}

export async function metaSetVersion(baseId: string, version: number): Promise<void> {
  await metaSet(baseId, {
    last_sync_version: version,
    last_sync_at: new Date().toISOString(),
  });
}

export async function metaSetStatus(
  baseId: string,
  status: SyncMeta['status'],
  error?: string
): Promise<void> {
  await metaSet(baseId, { status, error });
}

export async function metaClear(baseId: string): Promise<void> {
  await idbDelete(metaKey(baseId));
}

export async function metaClearAll(): Promise<void> {
  const keys = await idbKeysByPrefix(KEY_PREFIX);
  for (const key of keys) {
    await idbDelete(key);
  }
}
